import jsPDF from "jspdf";
import { toast } from "react-toastify";
import numenteLogo from "../assets/Numente.png";
import { BrushScriptMTFontBase64 } from "../assets/fonts/BrushScriptMTFont";

const SessionHistoryPDFGenerator = ({
  sessionFormData,
  clientName,
  providerName,
  providerCredentials,
  appointmentDate,
  appointmentTime,
  isSigned,
  signedDateFromDB,
  signedTimeFromDB,
  formatDate,
  formatDateTime,
  totalDuration,
  addendumData,
  clientDOB,
}) => {
  const generatePDF = async () => {
    try {
      const doc = new jsPDF();
      
      // Load Brush Script MT font for signature
      doc.addFileToVFS("BrushScriptMT.ttf", BrushScriptMTFontBase64);
      doc.addFont("BrushScriptMT.ttf", "BrushScriptMT", "normal");
      
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const headerHeight = 25;
      const contentWidth = pageWidth - (margin * 2);

      // Load logo image once and store as base64
      let logoBase64 = null;
      let logoWidth = 40;
      let logoHeight = 0;
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        await new Promise((resolve) => {
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              logoBase64 = canvas.toDataURL('image/png');
              logoHeight = (img.height / img.width) * logoWidth;
              resolve();
            } catch (error) {
              console.error("Error processing logo:", error);
              resolve();
            }
          };
          img.onerror = () => {
            console.error("Error loading logo image");
            resolve();
          };
          img.src = numenteLogo;
        });
      } catch (error) {
        console.error("Error loading logo:", error);
      }

      // Helper function to add header on each page
      const addHeader = () => {
        const headerY = 0;
        const HEADER_HEIGHT = 25;

        // Header background
        doc.setFillColor(245, 245, 245);
        doc.rect(0, headerY, pageWidth, HEADER_HEIGHT, "F");

        // Logo
        if (logoBase64) {
          const logoY = headerY + (HEADER_HEIGHT - logoHeight) / 2;
          doc.addImage(logoBase64, "PNG", margin, logoY, logoWidth, logoHeight);
        }

        // Right-side client text
        const clientNameText = clientName || "N/A";
        const dobFormatted = clientDOB
          ? new Date(clientDOB).toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            })
          : "N/A";

        const clientDOBText = `DOB: ${dobFormatted}`;

        // Measure widths using real fonts
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        const clientNameWidth = doc.getTextWidth(clientNameText);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const clientDOBWidth = doc.getTextWidth(clientDOBText);

        const maxTextWidth = Math.max(clientNameWidth, clientDOBWidth);

        // === TEXT COLOR BLACK ===
        doc.setTextColor(0, 0, 0);

        // Draw name (bold)
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(
          clientNameText,
          pageWidth - margin - maxTextWidth,
          headerY + HEADER_HEIGHT / 2 - 1
        );

        // Draw DOB (normal)
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text(
          clientDOBText,
          pageWidth - margin - maxTextWidth,
          headerY + HEADER_HEIGHT / 2 + 3
        );
      };


      // Add header to first page
      addHeader();


      // Set yPosition to start content below header
      let yPosition = headerHeight + 10;

      // Full width layout - no two columns
      const contentX = margin;
      let currentY = yPosition;
      let currentPage = 1;

      const hasAddendum = addendumData && Array.isArray(addendumData) && addendumData.length > 0;

      // Helper function to ensure we're on the correct page
      const ensurePage = (requiredHeight = 0) => {
        const currentPageHeight = doc.internal.pageSize.height;
        doc.setPage(currentPage);

        if (currentY + requiredHeight > currentPageHeight - 20) {
          currentPage++;
          doc.addPage();
          addHeader(); // Add header to new page
          currentY = headerHeight + 10;
        }

        doc.setPage(currentPage);
      };

      const addText = (text, x, y, maxWidth, fontSize = 10, isBold = false) => {
        doc.setFontSize(fontSize);
        doc.setFont("helvetica", isBold ? "bold" : "normal");
        const lines = doc.splitTextToSize(text || "N/A", maxWidth);
        doc.text(lines, x, y);
        return lines.length * (fontSize * 0.4);
      };

      const addSectionHeader = (title) => {
        ensurePage(20);
        currentY += 10;
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(33, 37, 41);
        doc.text(title, contentX, currentY);
        currentY += 8;
      };

      const addInlineField = (label, value) => {
        ensurePage(10);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        const labelText = `${label}:`;
        doc.text(labelText, contentX, currentY);
        
        doc.setFont("helvetica", "normal");
        const labelWidth = doc.getTextWidth(labelText);
        doc.text(value || "N/A", contentX + labelWidth + 5, currentY);
        currentY += 6;
      };

      const addTextArea = (label, value) => {
        ensurePage(20);
        if (label) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text(label + ":", contentX, currentY);
          currentY += 6;
        }
        const textValue = String(value || "");
        const textHeight = addText(textValue, contentX, currentY, contentWidth, 10, false);
        currentY += textHeight + 8;
      };

      const addBulletList = (items) => {
        if (!items || !Array.isArray(items) || items.length === 0) {
          return;
        }
        ensurePage(items.length * 6);
        items.forEach((item) => {
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.text("•", contentX, currentY);
          const itemHeight = addText(String(item), contentX + 6, currentY, contentWidth - 6, 10, false);
          currentY += Math.max(itemHeight, 6) + 2;
        });
        currentY += 4;
      };

      addSectionHeader("Appointment Details");

      // Appointment Details - Two column layout with inline labels and values
      const halfWidth = contentWidth / 2;
      
      // Row 1: Client and Provider (inline)
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      const clientLabel = "Client:";
      doc.text(clientLabel, contentX, currentY);
      doc.setFont("helvetica", "normal");
      const clientLabelWidth = doc.getTextWidth(clientLabel);
      const clientNameValue = clientName || "N/A";
      doc.text(clientNameValue, contentX + clientLabelWidth + 5, currentY);
      
      doc.setFont("helvetica", "bold");
      const providerLabel = "Provider:";
      doc.text(providerLabel, contentX + halfWidth, currentY);
      doc.setFont("helvetica", "normal");
      const providerLabelWidth = doc.getTextWidth(providerLabel);
      doc.text(providerName, contentX + halfWidth + providerLabelWidth + 5, currentY);
      currentY += 10;

      // Row 2: Date and Scheduled Time (inline)
      doc.setFont("helvetica", "bold");
      const dateLabel = "Date:";
      doc.text(dateLabel, contentX, currentY);
      doc.setFont("helvetica", "normal");
      const dateLabelWidth = doc.getTextWidth(dateLabel);
      doc.text(formatDate(appointmentDate) || "N/A", contentX + dateLabelWidth + 5, currentY);
      
      doc.setFont("helvetica", "bold");
      const scheduledTimeLabel = "Scheduled Time:";
      doc.text(scheduledTimeLabel, contentX + halfWidth, currentY);
      doc.setFont("helvetica", "normal");
      const scheduledTimeLabelWidth = doc.getTextWidth(scheduledTimeLabel);
      doc.text(appointmentTime || "N/A", contentX + halfWidth + scheduledTimeLabelWidth + 5, currentY);
      currentY += 10;

      // Row 3: Time In and Time Out (inline)
      doc.setFont("helvetica", "bold");
      const timeInLabel = "Time In:";
      doc.text(timeInLabel, contentX, currentY);
      doc.setFont("helvetica", "normal");
      const timeInLabelWidth = doc.getTextWidth(timeInLabel);
      doc.text(sessionFormData.timeIn || "N/A", contentX + timeInLabelWidth + 5, currentY);
      
      doc.setFont("helvetica", "bold");
      const timeOutLabel = "Time Out:";
      doc.text(timeOutLabel, contentX + halfWidth, currentY);
      doc.setFont("helvetica", "normal");
      const timeOutLabelWidth = doc.getTextWidth(timeOutLabel);
      doc.text(sessionFormData.timeOut || "N/A", contentX + halfWidth + timeOutLabelWidth + 5, currentY);
      currentY += 10;

      // Row 4: Duration (inline)
      doc.setFont("helvetica", "bold");
      const durationLabel = "Session Length:";
      doc.text(durationLabel, contentX, currentY);
      doc.setFont("helvetica", "normal");
      const durationLabelWidth = doc.getTextWidth(durationLabel);
      const durationText = totalDuration ? `${totalDuration}m` : "N/A";
      doc.text(durationText, contentX + durationLabelWidth + 5, currentY);
      currentY += 10;

      // Was client at home (inline)
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      const wasHomeLabel = "Was the client at home?:";
      const wasHomeText = sessionFormData.wasClientAtHome
        ? sessionFormData.wasClientAtHome.charAt(0).toUpperCase() + sessionFormData.wasClientAtHome.slice(1)
        : "N/A";
      doc.text(wasHomeLabel, contentX, currentY);
      doc.setFont("helvetica", "normal");
      const wasHomeLabelWidth = doc.getTextWidth(wasHomeLabel);
      doc.text(wasHomeText, contentX + wasHomeLabelWidth + 5, currentY);
      currentY += 10;



      // 📋 --- PRESENTING CONCERNS (FULL WIDTH) ---
      addSectionHeader("Presenting Concerns");
      addTextArea("Reason for seeking services", sessionFormData.reasonForSeekingServices || "");

      if (sessionFormData.selectedConcerns && Array.isArray(sessionFormData.selectedConcerns) && sessionFormData.selectedConcerns.length > 0) {
        addBulletList(sessionFormData.selectedConcerns);
      }

      // 📋 --- HISTORY OF PRESENTING CONCERNS (FULL WIDTH) ---
      addSectionHeader("History of Presenting Concerns");
      addInlineField("Duration", sessionFormData.duration || "");
      addInlineField("Frequency", sessionFormData.frequency || "");
      addInlineField("Severity", sessionFormData.severity || "");

      ensurePage(15);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Notes on history, past interventions", contentX, currentY);
      currentY += 6;
      addTextArea("", sessionFormData.freeTextHistory || "");

      // 📋 --- MEDICAL & PSYCHIATRIC HISTORY (FULL WIDTH) ---
      addSectionHeader("Medical & Psychiatric History");

      ensurePage(10);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Medical Conditions", contentX, currentY);
      currentY += 6;

      if (sessionFormData.selectedMedicalConditions && Array.isArray(sessionFormData.selectedMedicalConditions) && sessionFormData.selectedMedicalConditions.length > 0) {
        addBulletList(sessionFormData.selectedMedicalConditions);
        if (sessionFormData.selectedMedicalConditions.includes("Other") && sessionFormData.otherMedicalConditionText) {
          addTextArea("", sessionFormData.otherMedicalConditionText);
        }
      }

      addTextArea("Current Medications", sessionFormData.currentMedications || "");

      ensurePage(10);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Prior Psychiatric Care", contentX, currentY);
      currentY += 6;

      if (sessionFormData.selectedPastPsychiatric && Array.isArray(sessionFormData.selectedPastPsychiatric) && sessionFormData.selectedPastPsychiatric.length > 0) {
        addBulletList(sessionFormData.selectedPastPsychiatric);
      }

      // 📋 --- SUBSTANCE USE (FULL WIDTH) ---
      addSectionHeader("Substance Use");
      addInlineField("Alcohol", sessionFormData.alcohol || "");
      if (sessionFormData.alcoholDetails && sessionFormData.alcoholDetails.trim() !== "") {
        addTextArea("Details (Frequency, Amount, Impact)", sessionFormData.alcoholDetails);
      }
      addInlineField("Cannabis", sessionFormData.cannabis || "");
      if (sessionFormData.cannabisDetails && sessionFormData.cannabisDetails.trim() !== "") {
        addTextArea("Details (Frequency, Amount, Impact)", sessionFormData.cannabisDetails);
      }
      addInlineField("Tobacco", sessionFormData.tobacco || "");
      if (sessionFormData.tobaccoDetails && sessionFormData.tobaccoDetails.trim() !== "") {
        addTextArea("Details (Frequency, Amount, Impact)", sessionFormData.tobaccoDetails);
      }
      addInlineField("Illicit Drugs", sessionFormData.illicitDrugs || "");
      if (sessionFormData.illicitDrugsDetails && sessionFormData.illicitDrugsDetails.trim() !== "") {
        addTextArea("Details (Frequency, Amount, Impact)", sessionFormData.illicitDrugsDetails);
      }

      // 📋 --- FAMILY & SOCIAL HISTORY (FULL WIDTH) ---
      addSectionHeader("Family & Social History");
      addInlineField("Living Situation", sessionFormData.livingSituation || "");

      ensurePage(10);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Support System:", contentX, currentY);
      currentY += 6;

      if (sessionFormData.selectedSupportSystem && Array.isArray(sessionFormData.selectedSupportSystem) && sessionFormData.selectedSupportSystem.length > 0) {
        addBulletList(sessionFormData.selectedSupportSystem);
      }

      addTextArea("Additional Notes", sessionFormData.familySocialNotes || "");

      // 📋 --- EDUCATION & OCCUPATION (FULL WIDTH) ---
      addSectionHeader("Education & Occupation");
      addInlineField("Highest Education Level", sessionFormData.highestEducationLevel || "");
      addInlineField("Employment Status", sessionFormData.employmentStatus || "");
      addTextArea("Current job/role", sessionFormData.currentJobRole || "");

      // 📋 --- MENTAL STATUS EXAM (FULL WIDTH) ---
      addSectionHeader("Mental Status Exam");
      addInlineField("Appearance", sessionFormData.appearance || "");
      addInlineField("Behavior", sessionFormData.behavior || "");
      addInlineField("Speech", sessionFormData.speech || "");
      addInlineField("Mood", sessionFormData.mood || "");
      addInlineField("Affect", sessionFormData.affect || "");
      addInlineField("Thought Process", sessionFormData.thoughtProcess || "");
      addInlineField("Orientation", sessionFormData.orientation || "");
      addInlineField("Insight/Judgment", sessionFormData.insightJudgment || "");

      // 📋 --- RISK & SAFETY (FULL WIDTH) ---
      addSectionHeader("Risk & Safety");
      addInlineField("Suicide Risk", sessionFormData.suicideRisk || "");
      addInlineField("Homicide Risk", sessionFormData.homicideRisk || "");
      addInlineField("Abuse/Neglect", sessionFormData.abuseNeglect || "");
      addTextArea("Safety Plan Notes", sessionFormData.safetyPlanNotes || "");

      // 📋 --- STRENGTH & RESOURCES (FULL WIDTH) ---
      addSectionHeader("Strengths & Resources");
      ensurePage(10);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Select all that apply:", contentX, currentY);
      currentY += 6;

      if (sessionFormData.selectedStrengths && Array.isArray(sessionFormData.selectedStrengths) && sessionFormData.selectedStrengths.length > 0) {
        addBulletList(sessionFormData.selectedStrengths);
      }

      addTextArea("Client's own description of strengths", sessionFormData.clientStrengthsDescription || "");

      // 📋 --- DIAGNOSIS (FULL WIDTH) ---
      addSectionHeader("Diagnosis");
      const primaryDiagFull = `${sessionFormData.primaryDiagnosis || ""} ${sessionFormData.primaryDiagnosisDescription || ""}`.trim();
      addTextArea("Primary Diagnosis", primaryDiagFull || "");

      const secondaryDiagFull = `${sessionFormData.secondaryDiagnosis || ""} ${sessionFormData.secondaryDiagnosisDescription || ""}`.trim();
      if (secondaryDiagFull) {
        addTextArea("Secondary Diagnosis", secondaryDiagFull);
      }

      const tertiaryDiag = `${sessionFormData.tertiaryDiagnosis || ""} ${sessionFormData.tertiaryDiagnosisDescription || ""}`.trim();
      if (tertiaryDiag) {
        addTextArea("Tertiary Diagnosis", tertiaryDiag);
      }

      // 📋 --- CARE PLAN (FULL WIDTH) ---
      addSectionHeader("Care Plan");
      addTextArea("Client Goal", sessionFormData.clientGoal || "");
      addTextArea("Objectives", sessionFormData.objectives || "");
      addTextArea("Interventions", sessionFormData.interventions || "");
      addInlineField("Frequency", sessionFormData.sessionFrequency || "");
      // addInlineField("Review Date", sessionFormData.reviewDate ? formatDate(sessionFormData.reviewDate) : "N/A");

      // 📋 --- ADDENDUM SECTION (FULL WIDTH) ---
      if (hasAddendum) {
        ensurePage(20);
        addSectionHeader("Addendum");

        addendumData.forEach((addendum, index) => {
          if (addendum.addendumText) {
            ensurePage(15);
            // Always show addendum number (index + 1)
            doc.setFontSize(11);
            doc.setFont("helvetica", "bold");
            doc.text(`Addendum # ${index + 1}`, contentX, currentY);
            currentY += 6;
            addTextArea("", addendum.addendumText);
          }
        });
      }

      // 📋 --- PROVIDER SIGNATURE (FULL WIDTH) ---
      if (isSigned || sessionFormData.isSigned) {
        ensurePage(50);
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Provider Signature", contentX, currentY);
        currentY += 8;

        // Separator line
        doc.setLineWidth(0.5);
        doc.setDrawColor(200, 200, 200);
        doc.line(contentX, currentY, contentX + 40, currentY);
        currentY += 10;

        // Signature box background (light blue)
        const boxHeight = 60;
        const boxWidth = contentWidth;
        doc.setFillColor(236, 248, 254);
        doc.roundedRect(contentX, currentY, boxWidth, boxHeight, 2, 2, 'F');

        // Signature text (cursive style - matching SignAndLockCard)
        // Using Brush Script MT font for authentic signature appearance
        doc.setFontSize(32);
        doc.setFont("BrushScriptMT", "normal");
        doc.setTextColor(26, 26, 26);
        doc.text(providerName || "N/A", contentX + 10, currentY + 20);

        // Signed by text
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(102, 102, 102);
        doc.text(`Signed by ${providerName || "N/A"}, ${providerCredentials || "LPC"}`, contentX + 10, currentY + 28);

        // Date and time
        if (signedDateFromDB && signedTimeFromDB) {
          doc.text(`${signedDateFromDB}; ${signedTimeFromDB}`, contentX + 10, currentY + 36);
        }

        currentY += boxHeight + 15;
      }

      // 📋 --- ADDENDUM SIGNATURES (FULL WIDTH) ---
      if (hasAddendum) {
        addendumData.forEach((addendum, index) => {
          if (addendum.addendumSigned === true && addendum.addendumSignedAt) {
            ensurePage(50);

            const addendumNumber = index + 1;
            const addendumSignedDate = new Date(addendum.addendumSignedAt).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric"
            });
            const addendumSignedTime = new Date(addendum.addendumSignedAt).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true
            });

            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text(`Provider Signature - Addendum # ${addendumNumber}`, contentX, currentY);
            currentY += 8;

            // Separator line
            doc.setLineWidth(0.5);
            doc.setDrawColor(200, 200, 200);
            doc.line(contentX, currentY, contentX + 40, currentY);
            currentY += 10;

            // Signature box background (light blue)
            const boxHeight = 60;
            const boxWidth = contentWidth;
            doc.setFillColor(236, 248, 254);
            doc.roundedRect(contentX, currentY, boxWidth, boxHeight, 2, 2, 'F');

            // Signature text (cursive style - matching SignAndLockCard)
            // Using Brush Script MT font for authentic signature appearance
            doc.setFontSize(32);
            doc.setFont("BrushScriptMT", "normal");
            doc.setTextColor(26, 26, 26);
            doc.text(providerName || "N/A", contentX + 10, currentY + 20);

            // Signed by text
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(102, 102, 102);
            doc.text(`Signed by ${providerName || "N/A"}, ${providerCredentials || "LPC"}`, contentX + 10, currentY + 28);

            // Date and time from addendum
            doc.text(`${addendumSignedDate}; ${addendumSignedTime}`, contentX + 10, currentY + 36);

            currentY += boxHeight + 15;
          }
        });
      }

      // Footer - Page numbers and header on each page
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        // Add header to each page
        addHeader();
        // Add page number
        doc.setFontSize(10);
        doc.setTextColor(120);
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      }

      // Generate filename based on note type
      const dateStr = formatDate(appointmentDate).replace(/\//g, "-").replace(/\s+/g, "_");
      const clientNameFormatted = clientName.replace(/\s+/g, "_");
      const noteType = hasAddendum ? "ProgressNote" : "InitialAssessment";
      const fileName = `${noteType}_${clientNameFormatted}_${dateStr}.pdf`;

      doc.save(fileName);
      toast.success("Note downloaded.");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("An error occurred while generating PDF.");
    }
  };

  return generatePDF;
};

export default SessionHistoryPDFGenerator;
